import { ENDPOINTS } from './endPoints';
import type {
  SAProductsStreamPayload,
  SASummaryStreamPayload,
  SACompleteStreamPayload,
  SAStreamCallbacks,
} from '../types/stylingAssistant.types';

/**
 * Stream styling assistant search results
 * Connects to FastAPI StreamingResponse endpoint and processes NDJSON chunks
 */
export const streamStylingSearch = (
  query: string,
  userId: string,
  chatId: string,
  callbacks: SAStreamCallbacks
): AbortController => {
  const abortController = new AbortController();

  const url = ENDPOINTS.STYLING_ASSISTANT.buildSearchUrl(query, userId, chatId);

  console.log('[SA Stream] Connecting to FastAPI StreamingResponse:', url);

  consumeNDJSONStream(url, abortController.signal, callbacks).catch((error) => {
    if (error.name !== 'AbortError') {
      console.error('[SA Stream] Fatal error:', error);
      callbacks.onError(error);
    }
  });

  return abortController;
};

/**
 * Consume a FastAPI StreamingResponse (NDJSON format)
 * Each line is a complete JSON object separated by \n
 */
async function consumeNDJSONStream(
  url: string,
  signal: AbortSignal,
  callbacks: SAStreamCallbacks
): Promise<void> {
  const response = await fetch(url, {
    method: 'GET',
    signal,
    headers: {
      Accept: 'text/event-stream, application/json',
    },
  });

  if (!response.ok) {
    throw new Error(
      `Streaming request failed: ${response.status} ${response.statusText}`
    );
  }

  if (!response.body) {
    throw new Error('Response body is null — streaming not supported');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        if (buffer.trim()) {
          processLine(buffer.trim(), callbacks);
        }
        console.log('[SA Stream] Stream ended');
        callbacks.onStreamEnd?.();
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');

      for (let i = 0; i < lines.length - 1; i++) {
        const line = lines[i].trim();
        if (line) {
          processLine(line, callbacks);
        }
      }

      buffer = lines[lines.length - 1];
    }
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.log('[SA Stream] Request aborted by user');
    } else {
      throw error;
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Parse a single SSE line and route to the appropriate callback
 */
function processLine(rawLine: string, callbacks: SAStreamCallbacks): void {
  let line = rawLine.trim();
  if (line.startsWith('data:')) {
    line = line.substring(5).trim();
  }

  if (!line || line.startsWith(':')) {
    return;
  }

  try {
    const parsed = JSON.parse(line);

    if (!parsed.phase) {
      console.warn('[SA Stream] Received payload without phase:', parsed);
      return;
    }

    switch (parsed.phase) {
      case 'products': {
        const payload = parsed as SAProductsStreamPayload;
        const categoryKeys = Object.keys(payload.products || {});
        const totalProducts = categoryKeys.reduce(
          (sum, key) => sum + (payload.products[key]?.length || 0),
          0
        );
        console.log('[SA Stream] ✅ Products phase received:', {
          categories: categoryKeys,
          totalProducts,
          intentType: payload.intent_type,
        });
        callbacks.onProductsReceived(payload);
        break;
      }

      case 'summary': {
        const payload = parsed as SASummaryStreamPayload;
        console.log('[SA Stream] ✅ Summary phase received:', {
          summaryLength: payload.summary?.length || 0,
          followUpCount: payload.follow_up_questions?.length || 0,
        });
        callbacks.onSummaryReceived(payload);
        break;
      }

      case 'complete': {
        const payload = parsed as SACompleteStreamPayload;
        const categoryKeys = Object.keys(payload.products || {});
        const totalProducts = categoryKeys.reduce(
          (sum, key) => sum + (payload.products[key]?.length || 0),
          0
        );
        console.log('[SA Stream] ✅ Complete (cached) phase received:', {
          categories: categoryKeys,
          totalProducts,
          summaryLength: payload.summary?.length || 0,
          followUpCount: payload.follow_up_questions?.length || 0,
        });
        callbacks.onCompletePayloadReceived(payload);
        break;
      }

      default:
        console.warn('[SA Stream] Unknown phase:', parsed.phase);
    }
  } catch (error) {
    console.error(
      '[SA Stream] Failed to parse NDJSON line:',
      error,
      '\nLine:',
      line.substring(0, 300)
    );
  }
}

export default { streamStylingSearch };
